export const MASTER_TYPES = [
  'area',
  'cost_center',
  'chart_account',
  'category',
  'subcategory',
  'product',
] as const

export type MasterType = (typeof MASTER_TYPES)[number]

export interface MasterDataRecord {
  id: number
  name: string
  code: string | null
  erpId: string | null
  parentName?: string | null
}

export interface MasterDataUnificationSelection {
  masterType: MasterType
  records: MasterDataRecord[]
}

export interface MasterDataUnificationSummary {
  recordsAbsorbed: number
  referencesUpdated: number
  referencesDeduplicated: number
  subcategoriesReassigned: number
  subcategoriesAutoUnified: number
}

export interface MasterDataUnificationImpactItem {
  key: string
  label: string
  count: number
  deduplicated: number
}

export interface MasterDataUnificationConflict {
  code: string
  message: string
  entity: string
  rowIds: number[]
  blocking: boolean
}

export interface MasterDataUnificationPreview {
  previewToken: string
  expiresAt: string
  masterType: MasterType
  target: MasterDataRecord
  sources: MasterDataRecord[]
  summary: MasterDataUnificationSummary
  impact: MasterDataUnificationImpactItem[]
  conflicts: MasterDataUnificationConflict[]
  canExecute: boolean
  notice: string | null
}

export interface MasterDataUnificationResult {
  target: MasterDataRecord
  summary: MasterDataUnificationSummary
  impact: MasterDataUnificationImpactItem[]
}

export interface PreviewMasterDataUnificationRequest {
  masterType: MasterType
  selectedIds: number[]
  targetId: number
}

export const MASTER_DATA_UNIFICATION_ERROR_CODES = [
  'INVALID_SELECTION',
  'INCOMPATIBLE_SELECTION',
  'UNIFICATION_BLOCKED',
  'MASTER_RECORD_NOT_FOUND',
  'PREVIEW_STALE',
  'PREVIEW_TOKEN_INVALID',
  'UNEXPECTED_CASCADE',
  'UNIFICATION_FORBIDDEN',
  'MASTER_UNIFICATION_ERROR',
  'UNKNOWN',
] as const

export type MasterDataUnificationErrorCode =
  (typeof MASTER_DATA_UNIFICATION_ERROR_CODES)[number]

export class MasterDataUnificationError extends Error {
  constructor(
    public readonly code: MasterDataUnificationErrorCode,
    message: string,
    public readonly preview: MasterDataUnificationPreview | null = null,
  ) {
    super(message)
    this.name = 'MasterDataUnificationError'
  }
}
